import React from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Collapse, Popconfirm, Tag, Select, Space } from 'antd';
import RemedyEditor, { queryLanguageFor } from '../util/monaco-remedy';
import FieldsEditor from './FieldsEditor';

const { Panel } = Collapse;

// strips empty cells so the saved config stays clean
export const rowsToFieldsObject = (rows) => {
    const fields = {};
    rows.forEach((row) => {
        const { field, key, ...def } = row;
        if (!field) return;
        const cleanDef = {};
        Object.keys(def).forEach((k) => {
            if (def[k] !== undefined && def[k] !== null && def[k] !== '') {
                cleanDef[k] = def[k];
            }
        });
        fields[field] = cleanDef;
    });
    return fields;
};

/**
 * Editable panel for textConfig and anonymizeText entries.
 * entry: {form, query, fields:{FIELD:{action,size,value}}}
 */
const TextPanel = ({ entry, formFields, actions, onChange, onDelete, index, connectors, profiles }) => {
    const isMlEntry = (actions || []).indexOf('removePII') !== -1;
    const ql = queryLanguageFor(connectors, entry.connector);
    const rows = Object.keys(entry.fields || {}).map((field) => ({ field, ...entry.fields[field] }));

    const genExtra = () => (
        <Popconfirm
            title={`Remove ${entry.form} from configuration?`}
            onConfirm={(e) => { e.stopPropagation(); onDelete(); }}
            onCancel={(e) => e.stopPropagation()}
        >
            <DeleteOutlined onClick={(event) => event.stopPropagation()} />
        </Popconfirm>
    );

    return (
        <Collapse defaultActiveKey={['1']} style={{marginBottom: 16}}>
            <Panel key={1} header={<span>{entry.form}<Tag color="geekblue" style={{marginLeft: 8}}>{entry.connector || "remedy"}</Tag>{isMlEntry && entry.mode === 'review' && <Tag color="orange" style={{marginLeft: 4}}>review</Tag>}{isMlEntry && entry.profile && <Tag color="purple" style={{marginLeft: 4}}>{entry.profile}</Tag>}</span>} extra={genExtra()}>
                {isMlEntry && (
                    <Space style={{marginBottom: 10}} size="large">
                        <span>
                            <span style={{fontSize: 12, color: "#8c8c8c", marginRight: 8}}>Mode</span>
                            <Select
                                size="small"
                                value={entry.mode || 'auto'}
                                onChange={(v) => onChange({ ...entry, mode: v === 'auto' ? undefined : v })}
                                style={{width: 210}}
                                options={[
                                    {value: 'auto', label: 'Auto - apply directly'},
                                    {value: 'review', label: 'Review - service desk approves'}
                                ]}
                            />
                        </span>
                        <span>
                            <span style={{fontSize: 12, color: "#8c8c8c", marginRight: 8}}>Detection profile</span>
                            <Select
                                size="small"
                                value={entry.profile || ''}
                                onChange={(v) => onChange({ ...entry, profile: v || undefined })}
                                style={{width: 170}}
                                options={[{value: '', label: 'Default'}, ...((profiles || []).map((x) => ({value: x, label: x})))]}
                            />
                        </span>
                    </Space>
                )}
                <div style={{fontSize: 12, color: "#8c8c8c", marginBottom: 4}}>
                    Query ({ql.name}){ql.example && <span style={{marginLeft: 8, color: "#bfbfbf"}}>e.g. {ql.example}</span>}
                </div>
                <RemedyEditor
                    key={`text-bq-editor-${index}`}
                    queryLanguage={ql}
                    height={'60px'}
                    minimap={{ enabled: false }}
                    defaultValue={entry.query}
                    formFields={formFields}
                    form={entry.form}
                    onChange={(v) => onChange({ ...entry, query: v })}
                />
                <FieldsEditor
                    rows={rows}
                    formFields={formFields}
                    columns={[
                        { key: 'field', title: 'Field', type: 'field' },
                        { key: 'action', title: 'Action', type: 'select', options: actions },
                        { key: 'size', title: 'Size', type: 'number', width: 110 },
                        { key: 'value', title: 'Value', type: 'text' }
                    ]}
                    onChange={(newRows) => onChange({ ...entry, fields: rowsToFieldsObject(newRows) })}
                />
            </Panel>
        </Collapse>
    );
};

export default TextPanel;
